import { isProduction } from "@/utils/environment";

// Service constants
export const MAX_SERVICE_LINKS_TO_DISPLAY = 5;

// Event constants
export const EVENT_CONTACT_PREFIX = "contact_form_";
export const EVENT_CONTACT_GENERIC = `${EVENT_CONTACT_PREFIX}clicked_link`;
export const EVENT_CONTACT_LOG_IN = `${EVENT_CONTACT_PREFIX}user_login`;
export const EVENT_CONTACT_SIGN_UP = `${EVENT_CONTACT_PREFIX}user_signup`;
export const EVENT_CONTACT_CONTINUE_LOGGED_OUT = `${EVENT_CONTACT_PREFIX}continue_logged_out`;
export const EVENT_CONTACT_INIT = `${EVENT_CONTACT_PREFIX}opened_form`;
export const EVENT_CONTACT_STEP = `${EVENT_CONTACT_PREFIX}changed_node`;
export const EVENT_CONTACT_SUBMITTED = `${EVENT_CONTACT_PREFIX}submit`;
export const EVENT_CONTACT_ERROR = `${EVENT_CONTACT_PREFIX}error_sending_form`;
export const EVENT_CONTACT_SELF_SERVE_RESOURCE_CLICK = `${EVENT_CONTACT_PREFIX}self_serve_resource_click`;
export const EVENT_CONTACT_THIS_DIDNT_ANSWER = `${EVENT_CONTACT_PREFIX}this_didnt_answer_my_question`;
export const EVENT_CONTACT_FEDERATED_SEARCH = `federated_search_entered`;
export const EVENT_CONTACT_FEDERATED_SEARCH_SELECTION = `federated_search_selection`;
export const EVENT_CONTACT_COPILOT_ENGAGED = `${EVENT_CONTACT_PREFIX}copilot_engaged`;
export const EVENT_CONTACT_COPILOT_BACK_TO_FORM = `${EVENT_CONTACT_PREFIX}copilot_back_to_form`;

// CSS constants
export const CSS_CONTAINER_MAX_WIDTH = `${20 + 746 + 20}px`;
export const CSS_CONTAINER_DEFAULT_PADDING = "0 20px";

// Analytics events
export const EVENT_SYSTEM_NAME = "supportfrontend";
export const PAGE_VIEW_EVENT = "web_analytics.tracking.page_view.PageViewEvent";
export const USER_INTERACTION_EVENT =
  "web_analytics.tracking.user_interaction.UserInteractionEvent";

export const CONTACT_FORM_INTERACTION_EVENT =
  "support.supportfrontend.ContactFormInteractionEvent";

export const COPILOT_SOURCE = {
  widget: "chat-widget",
  fullscreen: "get-help",
} as const;

// Support constants
export const DAYS_FOR_PAID_SUPPORT_AFTER_TRIAL = 30;
export const USER_TIMEZONE_ZD_TAG_PREFIX = "browser-tz-";

// Contact form subject/message length limits.
// Field caps are aligned to Zendesk's actual limits: the ticket subject caps at
// 150 chars, and the end-user (Web Widget) comment body caps at 4096 chars
// (well under Zendesk's 65,535 hard limit). We don't use the web widget, but it
// seemed like a reasonable sanity limit. Query-param prefill caps are smaller
// to leave the user editing runway before hitting the field limit.
export const SUBJECT_MAX_LENGTH = 150;
export const MESSAGE_MAX_LENGTH = 4096;
export const SUBJECT_PREFILL_MAX_LENGTH = 150;
export const MESSAGE_PREFILL_MAX_LENGTH = 3000;

// Zendesk instance URLs
export const ZENDESK_PRODUCTION_URL = "https://zapier.zendesk.com";
export const ZENDESK_SANDBOX_URL = "https://zapier1772035478.zendesk.com";

/**
 * Get the appropriate Zendesk Support Portal URL based on environment
 * Used for schedule checks and other Zendesk Support API calls
 */
export const getZendeskSupportUrl = (isProd: boolean = isProduction) =>
  isProd ? ZENDESK_PRODUCTION_URL : ZENDESK_SANDBOX_URL;

// Zendesk custom field IDs (environment-specific)
// You can view the custom fields in Zendesk Admin here:
// https://zapier.zendesk.com/admin/objects-rules/tickets/ticket-fields
export const getZendeskCustomFieldIds = (isProd: boolean = isProduction) => ({
  BILLING_WHAT_WOULD_YOU_LIKE_TO_DO: isProd ? 360044340931 : 43810911856653,
  TASKS_WHATS_THE_ISSUE: isProd ? 360044340911 : 43810898193165,
  ACCESS_WHAT_WOULD_YOU_LIKE_TO_DO: isProd ? 360044340971 : 43810887544973,
  REPORT_BUG_OR_REQUEST_FEATURE: isProd ? 13794605780237 : 43810965586061,
  ACCOUNTS_WHAT_CAN_WE_HELP_WITH: isProd ? 360043779271 : 43810911487885,
  MORE_INFORMATION_REQUEST: isProd ? 360043776052 : 43810897815053,
  ABOUT_WHAT_CAN_WE_HELP_WITH: isProd ? 360043779291 : 43810887250573,
  ZAPS_WHAT_CAN_WE_HELP_WITH: isProd ? 360043779251 : 43810897897997,
  ERROR_WHAT_IS_THE_ERROR: isProd ? 360044340631 : 43810877597453,
  TROUBLESHOOTING_TELL_US_MORE: isProd ? 360043776252 : 43810911374349,
  OTHER_TOOLS_WHICH_TOOL: isProd ? 41867247181197 : 43810969197325, // In Zendesk, this field does not follow the `CF:{name}` format. Instead, it's just `Product` because it can be set in other ways besides the contact form.
  FIELDS_WHERE_IS_THE_PROBLEM: isProd ? 360044312032 : 43810862696205,
  // "CF:Which early access product?" dropdown — holds the selected Early Access
  // product (e.g. "cf-next-gen-zap-workflows"). The parent "Early Access
  // Products" category value is recorded in MORE_INFORMATION_REQUEST above.
  WHICH_EARLY_ACCESS_PRODUCT: isProd ? 47984791896461 : 47984834503949,
  // Critical-ticket field IDs are sourced dynamically from supportbff (single
  // source of truth) via /api/supportfrontend/v1/proxy/zendesk/field-ids — see
  // useCriticalTicketFieldConfig — so they are intentionally not duplicated here.
  // User identification fields (used by backend, adding for completeness and field utility page)
  ACCOUNT_ID: isProd ? 360044535432 : 43810887656077,
  CUSTOMUSER_ID: isProd ? 360044579811 : 43810863147789,
  // Support chat specific fields
  SUPPORT_RELEVANT_LINKS: isProd ? 21721707527053 : 43810916733453,
  SUPPORT_CHAT_DESCRIPTION: isProd ? 31806003244173 : 43810902323469,
  SUPPORT_TROUBLESHOOTING_STEPS: isProd ? 31805958109453 : 43810917122445,
  SUPPORT_ZENDESK_CONVERSATION_ID: isProd ? 31789855699213 : 43810924758285,
  SUPPORT_COPILOT_CONVERSATION_ID: isProd ? 41403922594957 : 43810904718477,
  SUPPORTBFF_CONVERSATION_ID: isProd ? 41403924753677 : 43810950671885,
});

// Backwards compatibility constant
export const ZENDESK_CUSTOM_FIELD_IDS = getZendeskCustomFieldIds();

// Zendesk CSAT constants
export const ZENDESK_CSAT_SURVEY_LOCALE = "en-us";
export const ZENDESK_EMAIL_CHANNEL = "web";
// If no CSAT survey exists, create one at https://<zendesk-url>/admin/objects-rules/rules/csat
// IDs for current active survey schema can be found via GET /api/v2/guide/en-us/surveys
export const ZENDESK_CSAT_SURVEY_ID = isProduction
  ? "01JNJY8TTRVPJK5ZNJ4QP7WKQC"
  : "01KKA2F3GBT2EQ9A3TKGKXK7BJ";
export const ZENDESK_CSAT_RATING_SCALE_ID = isProduction
  ? "01JNJY8R39CB7QFBGE3R96H033"
  : "01KKA2F1W3ZM1GDD0PJNYWNMX4";
export const ZENDESK_CSAT_OPEN_ENDED_COMMENT_ID = isProduction
  ? "01JNJY8R3ATG6MDDPBXW4Z8NB6"
  : "01KKA2F1W4GYP4MHM4RWKWNFZF";
// Zendesk requires us to setup an OAuth client: https://<zendesk-url>/admin/apps-integrations/apis/oauth-clients
// Used to connect to the Zendesk CSAT survey response endpoint to submit answers on an end user's behalf
// Client ID is not exposed in the UI. To fetch it, GET /api/v2/oauth/clients.json
export const ZENDESK_CSAT_SURVEY_OAUTH_CLIENT_ID = isProduction
  ? 41246911536269
  : 44144675877645;

// Images
export const DEFAULT_SERVICE_ICON_URL_GREY =
  "https://cdn.zapier.com/zapier/images/frontend/placeholder/128x128.grey.png";
export const DEFAULT_SERVICE_ICON_URL_ORANGE =
  "https://cdn.zapier.com/zapier/images/frontend/placeholder/128x128.png";
export const UPGRADE_TO_GET_BETTER_SUPPORT_CTA_HEADER =
  "https://zapier-images.imgix.net/storage/photos/1728c40b6993f25d35bd1cd39d0e9355.png";

// URLs and paths
export const ZAPIER_BASE_URL = process.env.NEXT_PUBLIC_ZAPIER_ORIGIN || "";
export const SUPPORTBFF_PATH = "/api/supportbff/v1/zapier_contact_form";
export const SUPPORTBFF_URL = `${ZAPIER_BASE_URL}${SUPPORTBFF_PATH}`;
export const GET_HELP_PATH = "/app/get-help";
export const GET_HELP_CTA = "get help";
export const PROD_HELP_CENTER_URL = "https://help.zapier.com/hc/en-us";
export const HELP_CENTER_SOURCE = "360000902071";
export const HELP_CENTER_TYPE = "knowledge_base";
export const PROD_COMMUNITY_URL = "https://community.zapier.com";

// React Query configuration for get-help queries
// These settings ensure:
// - Data stays fresh for 10 minutes before becoming stale
// - Cache persists for 12 minutes (longer than staleTime to avoid premature garbage collection)
// - Automatic refetch on window focus to catch login/account changes
export const QUERY_CONFIG = {
  staleTime: 10 * 60 * 1000, // 10 minutes
  cacheTime: 12 * 60 * 1000, // 12 minutes - longer than staleTime to avoid premature garbage collection
  refetchOnWindowFocus: true, // Refetch on window focus to catch login/account changes
  refetchOnReconnect: false, // Don't refetch on reconnect
  refetchOnMount: false, // Don't refetch on mount if data exists
} as const;
